const TELEMETRY_ENDPOINT_PATH = "/frontend-telemetry/v1/logs";
export const TELEMETRY_SOURCE = "hellocashflow-admin";
const SESSION_LOG_STORAGE_KEY = "HC_SESSION_LOGS";
const SESSION_LOG_FLUSH_DELAY_MS = 1_000;
const SESSION_LOG_MAX_RECORDS = 1_500;

type TelemetryMode = "on" | "off";

export type FrontendTelemetryError = {
    name?: string;
    message: string;
    stack?: string;
};

/**
 * Payload sent to `POST /frontend-telemetry/v1/logs`.
 *
 * @remarks
 * The backend accepts either a single record object or an array of records.
 *
 * @see HelloCashflowNodeJs/src/controllers/frontendTelemetryController.ts
 * @see docs/LOGGING.md (Logz.io `type` tagging contract)
 */
export type FrontendTelemetryLogRecord = {
    timestamp: string;
    level: string;
    severityText: string;
    message: string;
    attributes?: Record<string, unknown>;
    error?: FrontendTelemetryError;
    source?: string;
};

const readEnvValue = (key: string): string | undefined =>
    (import.meta.env as Record<string, string | undefined>)[key];

/**
 * Determines whether frontend telemetry should be emitted.
 *
 * @remarks
 * The backend telemetry endpoint is origin-allowlisted. In local/dev environments it is
 * commonly not configured for the admin origin, which can create noisy console 403s.
 *
 * Default behaviour: enabled in all environments (except tests).
 *
 * Override with `VITE_FRONTEND_TELEMETRY_MODE`:
 * - `on`  => emit (default; except in tests)
 * - `off` => never emit (useful when debugging without backend telemetry noise)
 */
const getTelemetryMode = (): TelemetryMode => {
    const configured = readEnvValue("VITE_FRONTEND_TELEMETRY_MODE");
    const normalized = configured?.trim().toLowerCase();

    if (normalized === "off") return "off";
    return "on";
};

const resolveTelemetryEndpoint = (): string | null => {
    const baseUrl = readEnvValue("VITE_API_URL");

    if (!baseUrl || !baseUrl.trim()) {
        return null;
    }

    try {
        return new URL(TELEMETRY_ENDPOINT_PATH, baseUrl).toString();
    } catch {
        return null;
    }
};

const isSessionLogEnabled = (): boolean => {
    if (import.meta.env.MODE === "test") {
        return false;
    }

    if (typeof window === "undefined") {
        return false;
    }

    const hostname = window.location.hostname.toLowerCase();
    return (
        hostname === "localhost" ||
        hostname === "127.0.0.1" ||
        hostname === "0.0.0.0" ||
        hostname.endsWith(".local")
    );
};

const sessionLogBuffer: FrontendTelemetryLogRecord[] = [];
let sessionLogFlushTimeout: ReturnType<typeof setTimeout> | null = null;
let hasSessionClearListener = false;

const pruneSessionLogBuffer = (): void => {
    const targetLength = SESSION_LOG_MAX_RECORDS;
    if (sessionLogBuffer.length <= targetLength) {
        return;
    }

    const excess = sessionLogBuffer.length - targetLength;
    sessionLogBuffer.splice(0, excess);
};

const writeSessionLogsToStorage = (): void => {
    if (!isSessionLogEnabled() || typeof localStorage === "undefined") {
        return;
    }

    try {
        localStorage.setItem(
            SESSION_LOG_STORAGE_KEY,
            JSON.stringify(sessionLogBuffer),
        );
    } catch {
        // Ignore storage failures; local logs are best-effort and should not block UX.
    }
};

const flushSessionLogs = (): Promise<void> => {
    return Promise.resolve().then(() => {
        if (!isSessionLogEnabled()) {
            return;
        }

        writeSessionLogsToStorage();
    });
};

const scheduleSessionLogWrite = (): void => {
    if (sessionLogFlushTimeout) {
        return;
    }

    sessionLogFlushTimeout = setTimeout(() => {
        sessionLogFlushTimeout = null;
        void flushSessionLogs();
    }, SESSION_LOG_FLUSH_DELAY_MS);
};

const clearSessionLogs = (): void => {
    sessionLogBuffer.length = 0;
    if (typeof localStorage === "undefined") {
        return;
    }

    try {
        localStorage.removeItem(SESSION_LOG_STORAGE_KEY);
        localStorage.setItem(SESSION_LOG_STORAGE_KEY, "[]");
    } catch {
        // Keep local session logs best-effort.
    }
};

const registerSessionLogClearListener = (): void => {
    if (typeof window === "undefined" || hasSessionClearListener) {
        return;
    }

    window.addEventListener("HC_CLEAR_LOGS", clearSessionLogs);
    hasSessionClearListener = true;
};

const initializeSessionLogging = (): void => {
    if (!isSessionLogEnabled()) {
        return;
    }

    registerSessionLogClearListener();
    clearSessionLogs();
};

const pushSessionLog = (record: FrontendTelemetryLogRecord): void => {
    if (!isSessionLogEnabled()) {
        return;
    }

    sessionLogBuffer.push(record);
    pruneSessionLogBuffer();
    scheduleSessionLogWrite();
};

initializeSessionLogging();

class FrontendTelemetrySink {
    private readonly endpoint: string | null = resolveTelemetryEndpoint();
    private readonly queue: FrontendTelemetryLogRecord[] = [];
    private flushTimeout: ReturnType<typeof setTimeout> | null = null;
    private isFlushing = false;

    enqueue(record: FrontendTelemetryLogRecord) {
        if (import.meta.env.MODE === "test" || !this.endpoint) {
            return;
        }

        const mode = getTelemetryMode();
        if (mode === "off") {
            return;
        }

        this.queue.push(record);

        // Defensive bound: prevent unbounded memory growth if the sink is misconfigured.
        const maxQueuedRecords = 50;
        if (this.queue.length > maxQueuedRecords) {
            this.queue.splice(0, this.queue.length - maxQueuedRecords);
        }

        // Flush quickly for error-level events; batch for lower levels.
        const normalizedLevel = record.level.toLowerCase();
        const flushSoon =
            normalizedLevel === "error" || normalizedLevel === "fatal";

        this.scheduleFlush(flushSoon ? 0 : 1_000);
    }

    private scheduleFlush(delayMs: number) {
        if (this.flushTimeout) {
            return;
        }

        this.flushTimeout = setTimeout(() => {
            this.flushTimeout = null;
            void this.flush().catch(() => {
                // Best-effort: avoid surfacing telemetry failures to the UI.
            });
        }, delayMs);
    }

    private async flushQueue(): Promise<void> {
        if (this.isFlushing || !this.endpoint || this.queue.length === 0) {
            return;
        }

        this.isFlushing = true;

        try {
            // Keep request payloads small to avoid hitting backend size limits and keepalive constraints.
            const batch = this.queue.splice(0, 25);

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 3_000);

            try {
                await fetch(this.endpoint, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(batch.length === 1 ? batch[0] : batch),
                    keepalive: true,
                    signal: controller.signal,
                });
            } finally {
                clearTimeout(timeoutId);
            }
        } finally {
            this.isFlushing = false;

            if (this.queue.length > 0) {
                this.scheduleFlush(1_000);
            }
        }
    }

    public async flush(): Promise<void> {
        await this.flushQueue();
    }
}

const telemetrySink = new FrontendTelemetrySink();

export const enqueueFrontendTelemetryRecord = (
    record: FrontendTelemetryLogRecord,
): void => {
    telemetrySink.enqueue(record);
    pushSessionLog(record);
};

export const flushFrontendTelemetry = async (): Promise<void> => {
    await Promise.all([telemetrySink.flush(), flushSessionLogs()]);
};

export const getFrontendSessionLogStorageKey = (): string =>
    SESSION_LOG_STORAGE_KEY;
